/**
 * VapiAssistant — floating mic-button widget powered by @vapi-ai/web.
 *
 * Mounted globally inside AppLayout (next to WalletModal + Toaster). Hidden on
 * /admin/* and on the /register full-page route. Lazy-loads the @vapi-ai/web
 * SDK on first click so the ~80kb chunk stays out of the initial bundle.
 *
 * Configuration is fetched at runtime from /api/vapi/config — keys are NOT
 * inlined into the Vite bundle, so the admin can rotate them via /admin/settings
 * without a redeploy. If the endpoint reports `enabled: false`, the widget
 * silently hides itself.
 *
 * navigate_to is handled as a client-side tool (the LLM tells the page to
 * router.push() to a known route). The path is hard-allowlisted before any
 * navigation so a prompt-injected assistant can't redirect users off-site.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Mic, PhoneOff, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { toast } from '@/stores/toastStore'

// ── Types — kept loose so we don't hard-couple to a specific @vapi-ai/web version ─

interface VapiInstance {
  start: (assistantId: string, overrides?: Record<string, unknown>) => Promise<unknown>
  stop: () => void
  on: (event: string, listener: (...args: unknown[]) => void) => void
  removeAllListeners?: () => void
}

interface VapiCtor {
  new (publicKey: string): VapiInstance
}

interface VapiConfigResponse {
  enabled: boolean
  publicKey?: string
  assistantId?: string
}

type CallStatus = 'idle' | 'connecting' | 'connected' | 'ending'

// ── navigate_to allowlist ──────────────────────────────────────────────────────

const ALLOWED_NAVIGATE_PREFIXES = [
  '/catalog',
  '/contracts',
  '/bundles',
  '/learn',
  '/dashboard',
  '/marketplace',
  '/profile',
] as const

function isAllowedPath(path: unknown): path is string {
  if (typeof path !== 'string') return false
  if (!path.startsWith('/')) return false
  if (path.includes('://')) return false
  if (path.startsWith('//')) return false
  if (path.startsWith('/admin')) return false
  return ALLOWED_NAVIGATE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix + '/'),
  )
}

// ── Module-scoped singleton ────────────────────────────────────────────────────
// React StrictMode mounts every component twice in dev. Re-instantiating Vapi
// would tear down the WebRTC pipe — keep one instance per tab.

let vapiSingleton: VapiInstance | null = null
let VapiClass: VapiCtor | null = null

async function getVapi(publicKey: string): Promise<VapiInstance> {
  if (vapiSingleton) return vapiSingleton
  if (!VapiClass) {
    const mod = await import('@vapi-ai/web')
    VapiClass = (mod.default ?? mod) as unknown as VapiCtor
  }
  vapiSingleton = new VapiClass(publicKey)
  return vapiSingleton
}

// ── Component ──────────────────────────────────────────────────────────────────

export function VapiAssistant() {
  const location = useLocation()
  const navigate = useNavigate()
  const userAddress = useAuthStore((s) => s.user?.address ?? null)

  const [status, setStatus] = useState<CallStatus>('idle')
  const [config, setConfig] = useState<VapiConfigResponse | null>(null)
  const [caption, setCaption] = useState('')
  const captionTimer = useRef<number | null>(null)

  // Stable refs for the always-current navigate fn + caller address so the
  // message handler doesn't need to re-bind on every render. Mutating refs
  // in-place during render trips react-hooks/refs (refs aren't reactive);
  // useEffect with no deps runs after each commit, which is the right time
  // to sync external mutable state.
  const navigateRef = useRef(navigate)
  const addressRef = useRef(userAddress)
  useEffect(() => {
    navigateRef.current = navigate
    addressRef.current = userAddress
  })

  // Hide on admin / register routes — admins don't need it and we don't want
  // the SDK chunk to load on internal pages.
  const isHidden = useMemo(() => {
    return location.pathname.startsWith('/admin') || location.pathname.startsWith('/register')
  }, [location.pathname])

  // ── Fetch config once on mount ──────────────────────────────────────────────
  useEffect(() => {
    if (isHidden) return
    let cancelled = false
    fetch('/api/vapi/config', { credentials: 'include' })
      .then((r) => (r.ok ? (r.json() as Promise<VapiConfigResponse>) : null))
      .then((data) => { if (!cancelled && data) setConfig(data) })
      .catch(() => { /* widget just stays hidden */ })
    return () => { cancelled = true }
  }, [isHidden])

  // ── Ephemeral caption — clears 4s after the last update ────────────────────
  function pushCaption(text: string) {
    setCaption(text)
    if (captionTimer.current) window.clearTimeout(captionTimer.current)
    captionTimer.current = window.setTimeout(() => setCaption(''), 4000)
  }

  // ── Start a call ────────────────────────────────────────────────────────────
  async function handleStart() {
    if (!config?.enabled || !config.publicKey || !config.assistantId) return
    setStatus('connecting')
    try {
      const vapi = await getVapi(config.publicKey)

      // Wire events once per singleton — guarded by a closed-over flag so
      // double-mount in StrictMode doesn't double-fire.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tagged = vapi as any
      if (!tagged.__sf_wired) {
        tagged.__sf_wired = true

        vapi.on('call-start', () => setStatus('connected'))
        vapi.on('call-end', () => {
          setStatus('idle')
          setCaption('')
        })
        vapi.on('error', (...args: unknown[]) => {
          const err = args[0]
          console.error('[vapi] error:', err)
          setStatus('idle')
          toast.error('Voice assistant error', 'Please try again.')
        })
        vapi.on('message', (...args: unknown[]) => {
          const msg = args[0] as
            | { type: string; transcript?: string; transcriptType?: string; role?: string; toolCalls?: unknown[]; toolCallList?: unknown[] }
            | undefined
          if (!msg) return

          // Live captions for partial transcripts (user side only — assistant
          // speech is already audible).
          if (msg.type === 'transcript' && typeof msg.transcript === 'string') {
            if (msg.role === 'user') pushCaption(msg.transcript)
          }

          // Client-side tool dispatch (navigate_to).
          if (msg.type === 'tool-calls') {
            const calls = (msg.toolCalls ?? msg.toolCallList ?? []) as Array<{
              id?: string
              function?: { name?: string; arguments?: string | Record<string, unknown> }
            }>
            for (const call of calls) {
              if (call?.function?.name !== 'navigate_to') continue
              let parsed: Record<string, unknown> = {}
              const raw = call.function.arguments
              if (typeof raw === 'string') {
                try { parsed = JSON.parse(raw) as Record<string, unknown> } catch { parsed = {} }
              } else if (raw && typeof raw === 'object') {
                parsed = raw
              }
              const path = parsed.path
              if (isAllowedPath(path)) {
                navigateRef.current(path)
              } else {
                console.warn('[vapi] navigate_to rejected:', path)
              }
            }
          }
        })
      }

      await vapi.start(config.assistantId, {
        metadata: { userAddress: addressRef.current },
      })
    } catch (err) {
      console.error('[vapi] start failed:', err)
      setStatus('idle')
      toast.error('Could not start voice assistant')
    }
  }

  function handleStop() {
    if (!vapiSingleton) {
      setStatus('idle')
      return
    }
    setStatus('ending')
    try { vapiSingleton.stop() } catch { /* ignore */ }
    // call-end will reset to idle; but if it doesn't fire within 1s, force it.
    window.setTimeout(() => setStatus((s) => (s === 'ending' ? 'idle' : s)), 1000)
  }

  // ── Render guards ──────────────────────────────────────────────────────────
  if (isHidden) return null
  if (!config?.enabled) return null

  const isActive = status === 'connected' || status === 'connecting' || status === 'ending'
  const label =
    status === 'connecting' ? 'Connecting…'
    : status === 'connected' ? 'End call'
    : status === 'ending' ? 'Ending…'
    : 'Talk to assistant'

  return (
    <div className="fixed bottom-6 right-6 z-[90] flex flex-col items-end gap-2 pointer-events-none">
      {caption && (
        <div className="pointer-events-none max-w-xs rounded-2xl border border-white/10 bg-black/80 px-4 py-2 text-sm text-white shadow-xl backdrop-blur">
          {caption}
        </div>
      )}
      <button
        type="button"
        onClick={isActive ? handleStop : handleStart}
        disabled={status === 'connecting' || status === 'ending'}
        aria-label={label}
        title={label}
        className={[
          'pointer-events-auto flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-all',
          'border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black',
          isActive
            ? 'bg-[#E8850C] border-[#E8850C] text-black hover:bg-[#FF9410] focus:ring-[#E8850C]'
            : 'bg-black border-white/15 text-white hover:border-[#E8850C] hover:text-[#E8850C] focus:ring-white/40',
          (status === 'connecting' || status === 'ending') ? 'opacity-70 cursor-wait' : '',
        ].join(' ')}
      >
        {status === 'connecting' || status === 'ending' ? (
          <Loader2 className="h-6 w-6 animate-spin" />
        ) : status === 'connected' ? (
          <PhoneOff className="h-6 w-6" />
        ) : (
          <Mic className="h-6 w-6" />
        )}
      </button>
    </div>
  )
}
